import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, FileText, Search, BarChart3, Target, ArrowRight } from 'lucide-react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

const resources = [
  {
    id: 'seo-checklist',
    title: '2026 SEO Checklist for Builders',
    description: 'A practical 23-point checklist covering everything from Google Business Profile to on-page optimisation. Tick off each item and watch your rankings climb.',
    icon: Search,
    category: 'SEO',
    pages: 8,
  },
  {
    id: 'gbp-guide',
    title: 'Google Business Profile Setup Guide',
    description: 'Step by step instructions for setting up and optimising your GBP listing. Includes screenshot walkthroughs and common mistakes to avoid.',
    icon: Target,
    category: 'Local SEO',
    pages: 12,
  },
  {
    id: 'speed-audit',
    title: 'Website Speed Audit Template',
    description: "The same template we use to audit client websites. Identify what's slowing your site down and prioritise fixes by impact.",
    icon: BarChart3,
    category: 'Performance',
    pages: 6,
  },
  {
    id: 'keyword-research',
    title: 'Local Keyword Research Template',
    description: 'A spreadsheet template for finding and organising keywords in your area. Includes formulas for estimating traffic and competition.',
    icon: FileText,
    category: 'SEO',
    pages: 4,
  },
];

const faqs = [
  {
    question: 'Are these resources really free?',
    answer: 'Yes. No credit card, no trial, no catch. We ask for your email so we can send the download link and occasionally share helpful tips. Unsubscribe any time.',
  },
  {
    question: 'Who are these guides for?',
    answer: 'Tradespeople in the UK who want to improve their online presence. Plumbers, electricians, builders, roofers, landscapers. The principles apply across trades.',
  },
  {
    question: 'How technical are these guides?',
    answer: 'Written for business owners, not developers. If you can use a smartphone, you can follow these guides. We explain jargon when we use it.',
  },
  {
    question: 'Can I share these with others?',
    answer: "Go for it. Forward them to mates, print them out, use them in your business. Just don't sell them or claim you wrote them.",
  },
];

export default function Resources() {
  const [selectedResource, setSelectedResource] = useState<typeof resources[0] | null>(null);
  const [formData, setFormData] = useState({ name: '', email: '', trade: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  // Set page metadata
  useEffect(() => {
    document.title = 'Free Resources | SEO Guides for Trades | SmartSite Solutions';
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Download free guides to improve your online presence. SEO checklists, Google Business Profile setup, and more for South Wales trades.');
    }

    // Add CollectionPage schema
    const schema = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Free Resources for Trades",
      "description": "Download free guides to improve your online presence. SEO checklists, Google Business Profile setup, and more for South Wales trades.",
      "url": "https://smartsite-lead-gen.lovable.app/resources",
      "mainEntity": {
        "@type": "ItemList",
        "itemListElement": resources.map((resource, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "name": resource.title,
          "description": resource.description
        }))
      }
    };

    let scriptTag = document.querySelector('script[data-schema="resources"]');
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.setAttribute('type', 'application/ld+json');
      scriptTag.setAttribute('data-schema', 'resources');
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(schema);

    return () => {
      // Cleanup schema on unmount
      const schemaScript = document.querySelector('script[data-schema="resources"]');
      if (schemaScript) {
        schemaScript.remove();
      }
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_key: 'ad32044d-23dc-46ab-b3b3-89aa110a09c3',
          subject: `Resource Download Request: ${selectedResource?.title}`,
          from_name: formData.name,
          email: formData.email,
          trade: formData.trade || 'Not specified',
          resource_requested: selectedResource?.title,
          resource_id: selectedResource?.id,
          botcheck: '', // Honeypot field
        }),
      });

      const result = await response.json();

      if (result.success) {
        setIsComplete(true);
        toast.success('Request submitted successfully!');
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeDialog = () => {
    setSelectedResource(null);
    setFormData({ name: '', email: '', trade: '' });
    setIsComplete(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-32 pb-24">
        <div className="container mx-auto px-4 lg:px-8">
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="inline-block px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm text-muted-foreground mb-6">
              Free Downloads
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Free Guides for{' '}
              <span className="text-gradient">South Wales Trades</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Practical resources to improve your online presence. No fluff, no upsells. 
              Just actionable advice you can use today.
            </p>
          </motion.div>

          {/* Resources Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid md:grid-cols-2 gap-6 mb-20"
          >
            {resources.map((resource, index) => (
              <motion.div
                key={resource.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + index * 0.05 }}
                className="glass-card p-8 group hover:border-white/20 transition-all duration-300"
              >
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-teal-500/20 to-teal-600/20 flex items-center justify-center flex-shrink-0">
                    <resource.icon className="w-7 h-7 text-teal-400" />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="px-2.5 py-0.5 rounded-full bg-white/5 text-xs text-muted-foreground">
                        {resource.category}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {resource.pages} pages
                      </span>
                    </div>
                    
                    <h3 className="font-display text-xl font-bold mb-3 group-hover:text-gradient transition-all duration-300">
                      {resource.title}
                    </h3>
                    
                    <p className="text-muted-foreground text-sm mb-6">
                      {resource.description}
                    </p>
                    
                    <Button 
                      onClick={() => setSelectedResource(resource)}
                      className="gradient-button"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Free
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* FAQ Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="font-display text-3xl font-bold text-center mb-12">
              Common Questions
            </h2>
            
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="glass-card p-6"
                >
                  <h3 className="font-display font-bold mb-2">{faq.question}</h3>
                  <p className="text-muted-foreground text-sm">{faq.answer}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </main>

      {/* Download Dialog */}
      <Dialog open={!!selectedResource} onOpenChange={closeDialog}>
        <DialogContent className="sm:max-w-md bg-background border-white/10">
          {!isComplete ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl">
                  Download: {selectedResource?.title}
                </DialogTitle>
                <DialogDescription>
                  Enter your details and we'll send the guide to your inbox.
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                {/* Honeypot field - hidden from users */}
                <input type="checkbox" name="botcheck" className="hidden" style={{ display: 'none' }} />
                
                <div>
                  <Input
                    placeholder="Your name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    maxLength={100}
                    className="bg-white/5 border-white/10"
                  />
                </div>
                
                <div>
                  <Input
                    type="email"
                    placeholder="Email address"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    maxLength={255}
                    className="bg-white/5 border-white/10"
                  />
                </div>
                
                <div>
                  <Select
                    value={formData.trade}
                    onValueChange={(value) => setFormData({ ...formData, trade: value })}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10">
                      <SelectValue placeholder="Your trade" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="plumber">Plumber</SelectItem>
                      <SelectItem value="electrician">Electrician</SelectItem>
                      <SelectItem value="builder">Builder</SelectItem>
                      <SelectItem value="roofer">Roofer</SelectItem>
                      <SelectItem value="landscaper">Landscaper</SelectItem>
                      <SelectItem value="painter">Painter & Decorator</SelectItem>
                      <SelectItem value="other">Other Trade</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <Button 
                  type="submit" 
                  className="gradient-button w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Get Your Free Guide'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                
                <p className="text-xs text-muted-foreground text-center">
                  We respect your privacy. Unsubscribe at any time.
                </p>
              </form>
            </>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center mx-auto mb-6">
                <Download className="w-8 h-8 text-white" />
              </div>
              <DialogTitle className="font-display text-xl mb-2">
                Check Your Inbox
              </DialogTitle>
              <DialogDescription>
                We've sent {selectedResource?.title} to {formData.email}. 
                It should arrive within a few minutes.
              </DialogDescription>
              <Button onClick={closeDialog} className="mt-6" variant="outline">
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}